import { ProFormColumnsType } from '@ant-design/pro-components';
import { gbPhoneNumberAntdRule } from 'utils/gbPhoneNumber';
import { Address } from '../common/Address';
import { BaseFormSchemaProps } from '../types';
import { btlApplicationFormApi } from '../../../api/btlApplicationForm';
import React from 'react';
import AutocompleteTree from './components/AutocompleteTree/AutocompleteTree';

type SolicitorsSchemaProps = BaseFormSchemaProps;

export enum WayToSelectSolicitor {
  PANEL_MEMBER = 'PANEL_MEMBER',
  NON_PANEL_MEMBER = 'NON_PANEL_MEMBER',
}

export const solicitorsFormSchema = (props: SolicitorsSchemaProps): ProFormColumnsType[] => [
  {
    valueType: 'dependency',
    name: ['isManualSolicitor', 'wayToSelectSolicitor'],
    columns: ({ isManualSolicitor, wayToSelectSolicitor }) => {
      const panelMemberComponent: ProFormColumnsType<any, 'dependency' | 'text'> = {
        dataIndex: 'panelMember',
        valueType: 'treeSelect',
        title: 'Select a Panel Member',
        width: 400,
        fieldProps: {
          disabled: isManualSolicitor,
          allowClear: true,
          treeNodeFilterProp: 'searchFields',
          treeDefaultExpandAll: true,
          popupMatchSelectWidth: false,
          showSearch: true,
        },
        request: async () => {
          return (await btlApplicationFormApi.getPanelMembers()).map(firm => ({
            title: (
              <>
                <div className={firm.registeredName ? 'leading-4 font-semibold' : 'font-semibold'}>{firm.name}</div>
                {firm.registeredName && <div className='text-xs text-gray-500'>{firm.registeredName}</div>}
              </>
            ),
            value: firm.id,
            searchFields: [firm.name, firm.registeredName].join(),
            children: firm.solicitors.map(solicitor => ({
              title: `${solicitor.firstName} ${solicitor.lastName}`,
              searchFields: [`${solicitor.firstName} ${solicitor.lastName}`, firm.name, firm.registeredName].join(),
              value: `${firm.id}-${solicitor.id}`,
            })),
          }));
        },
      };

      const nonPanelMemberComponent: ProFormColumnsType<any, 'dependency' | 'text'> = {
        title: 'Search by registered name',
        dataIndex: 'registeredName',
        fieldProps: {
          allowClear: true,
          showSearch: true,
        },
        renderFormItem: (_, { value, onChange }, form) => {
          return <AutocompleteTree disabled={isManualSolicitor} form={form} value={value} onChange={onChange} />;
        },
      };

      return [
        {
          title: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.panelMember.title' }),
          dataIndex: 'wayToSelectSolicitor',
          valueType: 'radioButton',
          fieldProps: form => ({
            options: [
              {
                label: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.panelMember.options.panelMember' }),
                value: WayToSelectSolicitor.PANEL_MEMBER,
              },
              {
                label: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.panelMember.options.nonPanelMember' }),
                value: WayToSelectSolicitor.NON_PANEL_MEMBER,
              },
            ],
            buttonStyle: 'solid',
            disabled: isManualSolicitor,
            value:
              form && typeof form.getFieldValue === 'function' ? form.getFieldValue('refinancePurpose') : undefined,
          }),
        } as ProFormColumnsType<any, 'dependency' | 'text'>,
        wayToSelectSolicitor === WayToSelectSolicitor.PANEL_MEMBER ? panelMemberComponent : undefined,
        wayToSelectSolicitor === WayToSelectSolicitor.NON_PANEL_MEMBER ? nonPanelMemberComponent : undefined,
      ].filter(Boolean) as ProFormColumnsType<any, 'dependency' | 'text'>[];
    },
  },
  {
    dataIndex: 'isManualSolicitor',
    valueType: 'switch',
    formItemProps: {
      valuePropName: 'checked',
    },
    fieldProps: {
      checkedChildren: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.isManualSolicitor.checked' }),
      unCheckedChildren: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.isManualSolicitor.unchecked' }),
    },
  },
  {
    valueType: 'dependency',
    name: ['isManualSolicitor'],
    columns: ({ isManualSolicitor }) => {
      if (isManualSolicitor) {
        return [
          {
            title: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.firmName.label' }),
            dataIndex: 'firmName',
            valueType: 'text',
            fieldProps: {
              allowClear: false,
            },
            formItemProps: {
              hasFeedback: true,
              rules: [{ required: true }],
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.solicitorName.label' }),
            dataIndex: 'solicitorName',
            valueType: 'text',
            fieldProps: {
              allowClear: false,
            },
            formItemProps: {
              hasFeedback: true,
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.emailAddress.label' }),
            dataIndex: 'emailAddress',
            valueType: 'text',
            fieldProps: {
              allowClear: false,
            },
            formItemProps: {
              rules: [{ type: 'email' }, { required: true }],
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.phoneNumber.label' }),
            dataIndex: 'phoneNumber',
            valueType: 'text',
            fieldProps: {
              allowClear: false,
            },
            formItemProps: {
              hasFeedback: true,
              rules: [
                gbPhoneNumberAntdRule(props.formatMessage({ id: 'common.error.validPhoneNumber' })),
                { required: true },
              ],
            },
          },
          {
            renderFormItem: () => (
              <>
                <h3>{props.formatMessage({ id: 'btlApplicationForm.SOLICITORS.address.header' })}</h3>
                <Address isEditable={props.isFormEditable} />
              </>
            ),
          },
        ];
      }

      return [];
    },
  },
];
